import { Box, Link, Stack, useMediaQuery, useTheme } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import CAppBreadcrumbs from '../../../app/components/CAppBreadcrumbs';
import CAppSubTitle from '../../../app/components/CAppSubTitle';
import CAppTitle from '../../../app/components/CAppTitle';
import AppCustomIcon from '../../../app/features/icon/AppIcon';
import AppLoaderPage from '../../../app/features/loader/page/AppLoaderPage';
import HAppRoutes from '../../../app/features/routes/helpers/HAppRoutes';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import CInvestDisclaimer from '../../investor/components/CInvestDisclaimer';
import useInvestExpertises from '../../investor/hooks/expertise/useInvestExpertises';
import useInvestFunds from '../../investor/hooks/fund/useInvestFunds';
import IFundType from '../../investor/interfaces/expertise/IFundType';
import IFund from '../../investor/interfaces/fund/IFund';
import InvestorRoutes from '../../investor/InvestorRoutes';
import DocumentRoutes from '../DocumentRoutes';
import FDocumentFilters from '../features/filters/FDocumentFilters';
import HDocumentFile from '../helpers/HDocumentFile';
import useDocumentFiles from '../hooks/useDocumentFiles';
import IDocumentFile from '../interfaces/IDocumentFile';
import IDocumentsFilters from '../interfaces/IDocumentsFilters';
import langDocumentFiles from '../langs/langDocumentFiles';
import VDocumentFilesTable from '../views/VDocumentFilesTable';

const PDocumentFund = () => {
	const authContext = useAuthContext();
	const routeParams = useParams();
	const investExpertiseTag = routeParams.investExpertiseTag;
	const investFundId = routeParams.investFundId;
	const { t } = useTranslation();
	const navigate = useNavigate();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { funds } = useInvestFunds(investExpertiseTag);
	const { documents } = useDocumentFiles();
	const investExpertises = useInvestExpertises();

	const [filters, setFilters] = useState<IDocumentsFilters>({
		fundIds: null,
		documentType: null,
		releasePeriod: null,
		format: null,
		fundType: null,
		fundVintage: null,
	});

	const [investFund, setInvestFund] = useState<IFund | null>(null);
	useEffect(() => {
		if (funds && investFundId) {
			setInvestFund(funds.find((x) => x.id === Number.parseInt(investFundId)) ?? null);
		}
	}, [funds, investFundId]);

	const [investExpertise, setInvestExpertise] = useState<IFundType | null>(null);
	useEffect(() => {
		if (investExpertises && investExpertiseTag) {
			setInvestExpertise(investExpertises.find((x) => x.tag === investExpertiseTag) ?? null);
		}
	}, [investExpertises, investExpertiseTag]);

	const [documentsForFund, setDocumentsForFund] = useState<IDocumentFile[] | null>(null);
	useEffect(() => {
		if (documents && investFund) {
			let documentsTmp = _.cloneDeep(documents);
			documentsTmp = documentsTmp.filter((x) => x.fund?.id === investFund.id);
			setDocumentsForFund(documentsTmp);
		}
	}, [investFund, documents]);

	const [documentsFiltered, setDocumentsFiltered] = useState<IDocumentFile[] | null>(null);
	useEffect(() => {
		if (documentsForFund) {
			let documentsTmp = _.cloneDeep(documentsForFund);
			if (filters.keywords) {
				const searchWords = filters.keywords.split(' ');
				searchWords.forEach((word) => {
					documentsTmp = documentsTmp.filter((x) =>
						HDocumentFile.cleanWord(x.name).includes(HDocumentFile.cleanWord(word)),
					);
				});
			}
			if (filters.documentType) {
				documentsTmp = documentsTmp.filter((x) => x.documentType === filters.documentType);
			}
			if (filters.investorIds) {
				documentsTmp = documentsTmp.filter(
					(x) => x.company === null || (filters.investorIds ?? []).includes(x.company?.id ?? -1),
				);
			}
			if (filters.format) {
				documentsTmp = documentsTmp.filter((x) => x.formatTag === filters.format);
			}
			if (filters.releasePeriod) {
				switch (filters.releasePeriod) {
					case '1_month':
						documentsTmp = documentsTmp.filter(
							(x) => moment(x.date).diff(moment().subtract(1, 'month'), 'days') > 0,
						);
						break;
					case '3_months':
						documentsTmp = documentsTmp.filter(
							(x) => moment(x.date).diff(moment().subtract(3, 'month'), 'days') > 0,
						);
						break;
					case '6_months':
						documentsTmp = documentsTmp.filter(
							(x) => moment(x.date).diff(moment().subtract(6, 'month'), 'days') > 0,
						);
						break;
					case '1_year':
						documentsTmp = documentsTmp.filter(
							(x) => moment(x.date).diff(moment().subtract(1, 'year'), 'days') > 0,
						);
						break;
					default:
						null;
				}
			}
			setDocumentsFiltered(documentsTmp);
		}
	}, [documentsForFund, filters]);

	return (
		<>
			{funds && investExpertise && investExpertiseTag && investFund && documentsForFund ? (
				<>
					<Helmet>
						<title>
							{authContext.authContext === 'employee' ? '[E] ' : ''} {t(langDocumentFiles.title.tag)} -
							{' ' + investFund.name + ' - '}
							{authContext.currentCompany ? authContext.currentCompany.name : ''}
						</title>
					</Helmet>
					<CAppBreadcrumbs
						items={[
							{
								label: t(langDocumentFiles.title.tag),
								location: HAppRoutes.getPath(DocumentRoutes.dashboard.tag),
							},

							{
								label: investExpertise.name,
								location: HAppRoutes.getPath(DocumentRoutes.documents_expertise.tag, {
									investExpertiseTag,
								}),
							},
							{
								label: investFund.name,
							},
						]}
					/>
					<Stack
						direction={!isMobile ? 'row' : 'column'}
						alignItems={!isMobile ? 'flex-end' : 'stretch'}
						justifyContent="space-between"
					>
						<CAppTitle title={investFund.name} />
						<Link
							sx={{
								marginBottom: '14px',
								fontSize: '0.875rem',
								fontWeight: 600,
								textDecoration: 'none',
								'&:hover': {
									textDecoration: 'underline',
								},
							}}
							onClick={() =>
								navigate(
									HAppRoutes.getPath(InvestorRoutes.fund.tag, {
										investExpertiseTag: investExpertise.tag,
										id: investFund.id.toString(),
									}),
								)
							}
						>
							<Stack direction="row" alignItems="center" spacing={1}>
								<Box sx={{ textAlign: 'right', width: '100%' }}>
									{t(langDocumentFiles.btn_go_to_fund_details.tag)}
								</Box>
								<AppCustomIcon tag="arrow-right" sx={{ fontSize: '0.875rem' }} />
							</Stack>
						</Link>
					</Stack>
					<Box sx={{ marginTop: '30px' }}>
						<FDocumentFilters
							orderFilters={[
								{
									filterTag: 'keywords',
									xs: 12,
									md: 3,
								},
								...(investExpertiseTag !== 'listed_assets'
									? [
											{
												filterTag: 'investorIds',
												xs: 12,
												md: 3,
											},
									  ]
									: []),
								{
									filterTag: 'documentType',
									xs: 12,
									md: 3,
								},
								{
									filterTag: 'releasePeriod',
									xs: 12,
									md: 3,
								},
							]}
							allDocuments={documentsForFund}
							allFunds={funds}
							filters={filters}
							onChange={(newFilters) => setFilters(newFilters)}
						/>
					</Box>
					<CAppSubTitle
						title={
							t(langDocumentFiles.x_documents.tag, {
								count: documentsFiltered ? documentsFiltered.length : 0,
							}) as unknown as string
						}
						icon={<AppCustomIcon tag="folders" />}
					/>

					<VDocumentFilesTable
						documents={documentsFiltered ?? []}
						displayColumns={['name', 'company.name', 'documentTypeName', 'date', 'id']}
					/>

					<CInvestDisclaimer />
				</>
			) : (
				<AppLoaderPage />
			)}
		</>
	);
};

export default PDocumentFund;
